import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { api, API_BASE, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Boxes, Plus, Pencil, Trash2, Loader2, MapPin, AlertTriangle, ScanLine, CheckCircle2, XCircle, HelpCircle, History, Wrench, Search, Paperclip, X, Download, ChevronDown, ListPlus, PackageSearch, Link2, MessageSquarePlus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

const STATUSES = {
  in_stock: { label: "In stock", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", color: "#059669" },
  out_of_stock: { label: "Out of stock", cls: "bg-rose-100 text-rose-800 border-rose-200", color: "#e11d48" },
  repair: { label: "Being repaired", cls: "bg-amber-100 text-amber-800 border-amber-200", color: "#d97706" },
  on_order: { label: "On order", cls: "bg-sky-100 text-sky-800 border-sky-200", color: "#0284c7" },
  on_loan: { label: "On loan", cls: "bg-purple-100 text-purple-800 border-purple-200", color: "#7c3aed" },
};

// No-stock parts float to the top of each base list.
const STATUS_ORDER = { out_of_stock: 0, repair: 1, on_order: 2, on_loan: 3, in_stock: 4 };

const ALT_IN_STOCK = { label: "Alternate In-Stock", cls: "bg-teal-100 text-teal-800 border-teal-200", color: "#0d9488" };

// ── Supplier availability (PartsBase + ILS), auto-fetched with a 2-lane queue ──
const _availCache = new Map();
const _availInflight = new Map();
const _availLanes = [Promise.resolve(), Promise.resolve()];
let _availLaneIdx = 0;
function fetchAvail(pn) {
  if (_availCache.has(pn)) return Promise.resolve(_availCache.get(pn));
  if (_availInflight.has(pn)) return _availInflight.get(pn);
  const lane = _availLaneIdx++ % _availLanes.length;
  const p = new Promise((resolve) => {
    _availLanes[lane] = _availLanes[lane].then(async () => {
      try {
        const { data } = await api.get(`/critical-spares/availability?pn=${encodeURIComponent(pn)}`);
        _availCache.set(pn, data);
        resolve(data);
      } catch {
        resolve(null);
      }
    });
  });
  _availInflight.set(pn, p);
  return p;
}

function AvailBadge({ pn }) {
  const key = (pn || "").trim().toUpperCase();
  const [d, setD] = useState(() => _availCache.get(key) || null);
  useEffect(() => {
    let live = true;
    if (!key) return undefined;
    fetchAvail(key).then((v) => { if (live) setD(v || false); });
    return () => { live = false; };
  }, [key]);
  if (!key) return null;
  if (d === null) {
    return <span className="font-mono text-[9px] uppercase tracking-widest text-gray-400 animate-pulse" data-testid={`cs-avail-loading-${key}`}>PB·ILS…</span>;
  }
  if (!d || (d.partsbase == null && d.ils == null)) return null;
  const tot = (d.partsbase || 0) + (d.ils || 0);
  return (
    <span
      className={`inline-flex items-center gap-0.5 font-mono text-[9px] uppercase tracking-widest rounded-full border px-1.5 py-0.5 ${tot > 0 ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-rose-300 bg-rose-50 text-rose-700 font-semibold"}`}
      title={tot > 0
        ? `Supplier availability (12h cache) — PartsBase: ${d.partsbase ?? "n/a"} listings · ILS: ${d.ils ?? "n/a"} parts`
        : "Availability issue — no suppliers found on PartsBase or ILS"}
      data-testid={`cs-avail-${key}`}
    >
      {tot === 0 && <AlertTriangle className="w-2.5 h-2.5 shrink-0" data-testid={`cs-avail-warn-${key}`} />}
      PB {d.partsbase ?? "—"} · ILS {d.ils ?? "—"}
    </span>
  );
}

function OpenOrderLine({ row, order, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const note = (row.open_order_notes || {})[order];
  const save = async () => {
    setBusy(true);
    try {
      const map = { ...(row.open_order_notes || {}) };
      const v = val.trim();
      if (v) map[order] = v; else delete map[order];
      await api.patch(`/critical-spares/${row.id}`, { open_order_notes: map });
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save note");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="font-mono text-[10px] text-sky-700">
      <span>{order}</span>
      {note && !editing && <span className="text-violet-700 font-semibold"> — {note}</span>}
      {canEdit && !editing && (
        <button
          type="button"
          className="ml-1 text-gray-400 hover:text-summit-primary align-middle"
          title={note ? "Edit order note" : "Add order note"}
          onClick={() => { setVal(note || ""); setEditing(true); }}
          data-testid={`cs-oo-note-btn-${row.id}-${order}`}
        >
          <MessageSquarePlus className="w-3 h-3 inline" />
        </button>
      )}
      {editing && (
        <span className="inline-flex items-center gap-1 ml-1 align-middle">
          <input
            className="border border-summit-hairline rounded px-1.5 py-0.5 text-[10px] w-48 bg-white text-gray-800"
            value={val}
            autoFocus
            placeholder="Note stays through re-scans…"
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            data-testid={`cs-oo-note-input-${row.id}`}
          />
          <button type="button" className="text-emerald-600 hover:text-emerald-700" onClick={save} disabled={busy} data-testid={`cs-oo-note-save-${row.id}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <Check className="w-3 h-3 inline" />}
          </button>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={() => setEditing(false)}>
            <X className="w-3 h-3 inline" />
          </button>
        </span>
      )}
    </div>
  );
}

// Derived status: a part that isn't in stock itself but whose alternate PN IS in stock at the same base.
const isAltInStock = (r, all) =>
  r.status !== "in_stock" &&
  (r.alternates || []).some((a) =>
    (all || []).some(
      (x) => x.id !== r.id && x.base === r.base && x.status === "in_stock" && x.part_number.toUpperCase() === (a || "").toUpperCase()
    )
  );

// CARs 705 fleet = ATR + Avro RJ + Dash 8 (DHC-8) combined summary tab.
const is705 = (f) => {
  const x = f || "";
  return x.startsWith("ATR") || x === "AVRO RJ" || x.startsWith("DASH") || x.startsWith("DHC");
};

// Avro RJ100 + RJ85 are tracked as a single "AVRO RJ" fleet.
const normFleet = (f) => {
  const x = (f || "").toUpperCase().trim();
  return /^(AVRO\s*)?RJ\s*(85|100)$/.test(x) ? "AVRO RJ" : x;
};

const fmtStamp = (s) => (s?.at ? `${new Date(s.at).toLocaleDateString()} ${new Date(s.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · ${s.by_name}` : null);

function StatusBadge({ status, alt }) {
  const m = alt ? ALT_IN_STOCK : (STATUSES[status] || STATUSES.in_stock);
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-widest ${m.cls}`}>
      {m.label}
    </span>
  );
}

function BasePie({ parts, base }) {
  const data = Object.entries(STATUSES)
    .map(([k, v]) => ({
      name: v.label,
      value: parts.filter((p) => p.status === k && !(k === "out_of_stock" && isAltInStock(p, parts))).length,
      color: v.color,
    }))
    .filter((d) => d.value > 0);
  if (data.length === 0) return null;
  return (
    <div className="flex flex-col items-center px-4 py-3 shrink-0" data-testid={`cs-pie-${base}`}>
      <div className="w-[150px] h-[150px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={36} outerRadius={64} paddingAngle={2} stroke="#fff" strokeWidth={1.5}>
              {data.map((d) => <Cell key={d.name} fill={d.color} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-0.5 mt-1">
        {data.map((d) => (
          <div key={d.name} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-600">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }} />
            {d.name} · {d.value}
          </div>
        ))}
      </div>
    </div>
  );
}

function MasterScanResultDialog({ result, onClose }) {
  return (
    <Dialog open={!!result} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="cs-master-scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ListPlus className="w-5 h-5 text-summit-primary" /> Master list scan — {result?.base}
          </DialogTitle>
          <DialogDescription className="sr-only">Master list import results</DialogDescription>
        </DialogHeader>
        {result && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600" data-testid="cs-master-scan-summary">
              <strong>{result.added_count}</strong> new part{result.added_count === 1 ? "" : "s"} added to {result.base} ({result.fleet}) as Out of stock.
              {result.skipped_count > 0 && <> {result.skipped_count} already on the list — left untouched.</>}
            </p>
            {result.added.length > 0 && (
              <div className="space-y-1.5">
                {result.added.map((m) => (
                  <div key={m.id} className="border border-summit-hairline rounded-md px-2.5 py-1.5 flex items-center gap-2 flex-wrap" data-testid={`cs-master-scan-added-${m.id}`}>
                    <span className="font-mono text-xs font-semibold">{m.part_number}</span>
                    {m.description && <span className="text-xs text-gray-600 truncate max-w-[200px]">* {m.description}</span>}
                    {(m.min_qty != null || m.max_qty != null) && (
                      <span className="ml-auto font-mono text-[10px] text-gray-500">min {m.min_qty ?? "—"} · max {m.max_qty ?? "—"}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {result.added.length === 0 && (
              <p className="text-sm text-gray-400" data-testid="cs-master-scan-empty">Nothing new — every part in the file is already on the master list.</p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={onClose} data-testid="cs-master-scan-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GeneralScanDialog({ result, onClose }) {
  return (
    <Dialog open={!!result} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="cs-general-scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-summit-primary" /> General stock scan
          </DialogTitle>
          <DialogDescription className="sr-only">All-bases stock cross-check results</DialogDescription>
        </DialogHeader>
        {result && (
          <div className="space-y-3">
            {(result.bases || []).map((b) => (
              <div key={b.base} className="border border-summit-hairline rounded-md px-3 py-2" data-testid={`cs-general-base-${b.base}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-display font-semibold text-sm flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-summit-primary" />{b.base}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-700">
                    {b.updated}/{b.updated + (b.not_found || []).length} in stock{b.marked_oos > 0 ? ` · ${b.marked_oos} → out` : ""}
                  </span>
                </div>
                {(b.not_found || []).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {(b.not_found || []).slice(0, 15).map((pn) => (
                      <span key={pn} className="font-mono text-[10px] border border-rose-200 bg-rose-50 text-rose-700 rounded px-1.5 py-0.5">{pn}</span>
                    ))}
                    {(b.not_found || []).length > 15 && (
                      <span className="text-[10px] text-gray-400 self-center">+{(b.not_found || []).length - 15} more missing</span>
                    )}
                  </div>
                )}
                {b.unknown_count > 0 && (
                  <div className="mt-1 font-mono text-[9px] uppercase text-gray-400">{b.unknown_count} unknown PN{b.unknown_count === 1 ? "" : "s"} in file</div>
                )}
              </div>
            ))}
            {(result.no_master || []).length > 0 && (
              <p className="text-xs text-gray-500" data-testid="cs-general-no-master">
                No master list yet — skipped: {result.no_master.join(", ")}
              </p>
            )}
            {Object.keys(result.skipped_bases || {}).length > 0 && (
              <div data-testid="cs-general-skipped">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Unrecognized bases in file — rows skipped</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {Object.entries(result.skipped_bases).map(([raw, n]) => (
                    <span key={raw} className="font-mono text-[10px] border border-amber-200 bg-amber-50 text-amber-800 rounded px-1.5 py-0.5">{raw} ×{n}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={onClose} data-testid="cs-general-scan-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GeneralScanFooter({ result }) {
  return (
    <>
      {(result.no_master || []).length > 0 && (
        <p className="text-xs text-gray-500">No master list yet — skipped: {result.no_master.join(", ")}</p>
      )}
      {Object.keys(result.skipped_bases || {}).length > 0 && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Unrecognized bases in file — rows skipped</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {Object.entries(result.skipped_bases).map(([raw, n]) => (
              <span key={raw} className="font-mono text-[10px] border border-amber-200 bg-amber-50 text-amber-800 rounded px-1.5 py-0.5">{raw} ×{n}</span>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function GeneralRepairDialog({ result, onClose }) {
  return (
    <Dialog open={!!result} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="cs-general-repair-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Wrench className="w-5 h-5 text-amber-600" /> General ER scan
          </DialogTitle>
          <DialogDescription className="sr-only">All-bases ER repair scan results</DialogDescription>
        </DialogHeader>
        {result && (
          <div className="space-y-3">
            {(result.bases || []).length === 0 && (
              <p className="text-sm text-gray-500" data-testid="cs-general-repair-empty">No ER parts matched any base's master list.</p>
            )}
            {(result.bases || []).map((b) => (
              <div key={b.base} className="border border-summit-hairline rounded-md px-3 py-2" data-testid={`cs-general-repair-base-${b.base}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-display font-semibold text-sm flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-summit-primary" />{b.base}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-amber-700">{b.updated} → being repaired</span>
                </div>
                {(b.matched || []).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {(b.matched || []).slice(0, 12).map((m) => (
                      <span key={m.id} className="font-mono text-[10px] border border-amber-200 bg-amber-50 text-amber-800 rounded px-1.5 py-0.5">{m.part_number}</span>
                    ))}
                    {(b.matched || []).length > 12 && <span className="text-[10px] text-gray-400 self-center">+{(b.matched || []).length - 12} more</span>}
                  </div>
                )}
                {(b.in_stock_updated || []).length > 0 && (
                  <div className="mt-1 font-mono text-[9px] uppercase text-gray-400">
                    {b.in_stock_updated.length} in-stock part{b.in_stock_updated.length === 1 ? "" : "s"} noted with repair serials
                  </div>
                )}
                {b.updated === 0 && (b.in_stock_updated || []).length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">No ER rows matched this base's master list.</p>
                )}
              </div>
            ))}
            <GeneralScanFooter result={result} />
          </div>
        )}
        <DialogFooter>
          <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={onClose} data-testid="cs-general-repair-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GeneralOODialog({ result, onClose }) {
  return (
    <Dialog open={!!result} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="cs-general-oo-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <PackageSearch className="w-5 h-5 text-sky-600" /> General OO scan
          </DialogTitle>
          <DialogDescription className="sr-only">All-bases open-orders scan results</DialogDescription>
        </DialogHeader>
        {result && (
          <div className="space-y-3">
            {(result.bases || []).map((b) => (
              <div key={b.base} className="border border-summit-hairline rounded-md px-3 py-2" data-testid={`cs-general-oo-base-${b.base}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-display font-semibold text-sm flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-summit-primary" />{b.base}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-sky-700">
                    {b.updated} matched · {b.status_changed} → on order · {b.cleared} cleared
                  </span>
                </div>
                {(b.matched || []).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {(b.matched || []).slice(0, 12).map((m) => (
                      <span key={m.id} className={`font-mono text-[10px] border rounded px-1.5 py-0.5 ${m.status_changed ? "border-sky-300 bg-sky-50 text-sky-800" : "border-summit-hairline text-gray-600"}`}>{m.part_number}</span>
                    ))}
                    {(b.matched || []).length > 12 && <span className="text-[10px] text-gray-400 self-center">+{(b.matched || []).length - 12} more</span>}
                  </div>
                )}
              </div>
            ))}
            <GeneralScanFooter result={result} />
          </div>
        )}
        <DialogFooter>
          <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={onClose} data-testid="cs-general-oo-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SUMMARY_STATUSES = [
  ["in_stock", "In stock", "bg-emerald-500", "text-emerald-700"],
  ["out_of_stock", "Out of stock", "bg-rose-500", "text-rose-700"],
  ["repair", "Being repaired", "bg-amber-500", "text-amber-700"],
  ["on_order", "On order", "bg-sky-500", "text-sky-700"],
  ["on_loan", "On loan", "bg-violet-500", "text-violet-700"],
];

function SummaryPanel({ rows }) {
  const byFleet = {};
  (rows || []).forEach((r) => { ((byFleet[r.fleet] ||= {})[r.base] ||= []).push(r); });
  const fleetNames = Object.keys(byFleet).sort();
  const ORD = { out_of_stock: 0, repair: 1, on_order: 2, on_loan: 3, in_stock: 4 };
  // Alternates count as ONE unit: union-find clusters; each cluster counts once
  // under its best status (in stock beats on loan/on order/repair/out of stock).
  const clusterCounts = (parts) => {
    const byPN = {};
    parts.forEach((p) => { byPN[p.part_number.toUpperCase()] = p; });
    const parent = {};
    parts.forEach((p) => { parent[p.id] = p.id; });
    const find = (x) => (parent[x] === x ? x : (parent[x] = find(parent[x])));
    parts.forEach((p) => (p.alternates || []).forEach((a) => {
      const q = byPN[(a || "").toUpperCase()];
      if (q) { const ra = find(p.id), rb = find(q.id); if (ra !== rb) parent[rb] = ra; }
    }));
    const groups = {};
    parts.forEach((p) => { (groups[find(p.id)] ||= []).push(p); });
    const counts = {};
    let total = 0;
    Object.values(groups).forEach((g) => {
      total += 1;
      const best = g.reduce((acc, p) => ((ORD[p.status] ?? -1) > (ORD[acc.status] ?? -1) ? p : acc));
      counts[best.status] = (counts[best.status] || 0) + 1;
    });
    return { counts, total };
  };
  return (
    <div className="mt-6 space-y-5" data-testid="cs-summary-panel">
      <div className="flex flex-wrap items-center gap-4">
        {SUMMARY_STATUSES.map(([k, label, dot]) => (
          <span key={k} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-500">
            <span className={`w-2.5 h-2.5 rounded-full ${dot}`} /> {label}
          </span>
        ))}
      </div>
      {fleetNames.map((f) => {
        const bases = Object.entries(byFleet[f]).sort(([a], [b]) => a.localeCompare(b));
        return (
          <div key={f} className="border border-summit-hairline rounded-lg bg-white" data-testid={`cs-summary-fleet-${f.replace(/\s+/g, "-")}`}>
            <div className="px-4 py-2.5 border-b border-summit-hairline bg-summit-glacier/50 rounded-t-lg font-display font-semibold">{f}</div>
            <div className="divide-y divide-summit-hairline">
              {bases.map(([base, parts]) => {
                const { counts, total } = clusterCounts(parts);
                return (
                  <div key={base} className="px-4 py-3 flex items-center gap-4 flex-wrap" data-testid={`cs-summary-base-${f.replace(/\s+/g, "-")}-${base}`}>
                    <span className="font-display font-semibold min-w-[3.5rem] inline-flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-summit-primary" />{base}
                    </span>
                    <div className="flex-1 min-w-[140px] h-2.5 rounded-full overflow-hidden bg-gray-100 flex">
                      {SUMMARY_STATUSES.map(([k, , dot]) => (counts[k] ? <div key={k} className={dot} style={{ width: `${(counts[k] / total) * 100}%` }} /> : null))}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      {SUMMARY_STATUSES.map(([k, label, dot, txt]) => (
                        <span key={k} title={label} className={`inline-flex items-center gap-1 font-mono text-[11px] ${counts[k] ? txt : "text-gray-300"}`}>
                          <span className={`w-2 h-2 rounded-full ${counts[k] ? dot : "bg-gray-200"}`} />{counts[k] || 0}
                        </span>
                      ))}
                      <span className="font-mono text-[10px] uppercase text-gray-400">/ {total}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {fleetNames.length === 0 && (
        <div className="border border-dashed border-summit-hairline rounded-lg py-12 text-center text-sm text-gray-400" data-testid="cs-summary-empty">
          No critical spares recorded yet.
        </div>
      )}
    </div>
  );
}

function ScanResultDialog({ result, onClose }) {
  return (
    <Dialog open={!!result} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="cs-scan-result-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-summit-primary" /> Cross-check — {result?.base}
          </DialogTitle>
          <DialogDescription className="sr-only">Stock cross-check results</DialogDescription>
        </DialogHeader>
        {result && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600" data-testid="cs-scan-summary">
              <strong>{result.updated}</strong> part{result.updated === 1 ? "" : "s"} confirmed in stock. The file was read in your browser only — nothing was uploaded or stored.
            </p>
            {result.matched.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-emerald-700">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Found in stock
                </div>
                <div className="mt-1.5 space-y-1.5">
                  {result.matched.map((m) => (
                    <div key={m.id} className="border border-emerald-100 bg-emerald-50/50 rounded-md px-2.5 py-1.5" data-testid={`cs-scan-matched-${m.id}`}>
                      <div className="font-mono text-xs font-semibold">{m.part_number}</div>
                      {m.serials.map((s, i) => (
                        <div key={i} className="font-mono text-[10px] text-gray-600">
                          S/N {s.serial || "—"}{s.location ? ` · ${s.location}` : ""}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {result.not_found.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-rose-700">
                  <XCircle className="w-3.5 h-3.5" /> Master list parts not in file ({result.not_found.length})
                </div>
                <p className="text-xs text-gray-500 mt-1" data-testid="cs-scan-oos-note">
                  {result.marked_oos > 0 ? `${result.marked_oos} marked Out of stock. ` : ""}Parts being repaired, on order or on loan keep their status.
                </p>
                <div className="mt-1 flex flex-wrap gap-1" data-testid="cs-scan-not-found">
                  {result.not_found.map((pn) => (
                    <span key={pn} className="font-mono text-[10px] border border-rose-200 bg-rose-50 text-rose-700 rounded px-1.5 py-0.5">{pn}</span>
                  ))}
                </div>
              </div>
            )}
            {(result.alt_in_stock_kept || []).length > 0 && (
              <p className="font-mono text-[10px] uppercase tracking-widest text-teal-700" data-testid="cs-scan-alt-kept">
                Alternate on shelf — shown as Alternate In-Stock: {result.alt_in_stock_kept.join(", ")}
              </p>
            )}
            {(result.unknown_count ?? result.unknown?.length ?? 0) > 0 && (
              <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-500" data-testid="cs-scan-unknown">
                <HelpCircle className="w-3.5 h-3.5 shrink-0" />
                {(result.unknown_count ?? result.unknown.length).toLocaleString()} part number{(result.unknown_count ?? result.unknown.length) === 1 ? "" : "s"} in the file {(result.unknown_count ?? result.unknown.length) === 1 ? "isn't" : "aren't"} on the master list
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={onClose} data-testid="cs-scan-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ObsolescencePanel({ items, canEdit, fleets, onChanged }) {
  const [notes, setNotes] = useState({});
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ part_number: "", fleet: "", description: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const fileInputs = useRef({});

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items || [];
    return (items || []).filter((r) =>
      [r.part_number, r.fleet, r.description, ...(r.obs_log || []).map((e) => e.note)].filter(Boolean).join(" ").toLowerCase().includes(s)
    );
  }, [items, q]);

  const addNote = async (r) => {
    const note = (notes[r.id] || "").trim();
    const file = files[r.id];
    if (!note && !file) { toast.error("Type the action note or attach a file first"); return; }
    setBusy(r.id);
    try {
      if (file) {
        const fd = new FormData();
        fd.append("note", note);
        fd.append("file", file);
        await api.post(`/critical-spares-obs/${r.id}/note-file`, fd);
      } else {
        await api.post(`/critical-spares-obs/${r.id}/note`, { note });
      }
      setNotes((m) => ({ ...m, [r.id]: "" }));
      setFiles((m) => ({ ...m, [r.id]: null }));
      if (fileInputs.current[r.id]) fileInputs.current[r.id].value = "";
      toast.success("Action recorded");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(null);
    }
  };

  const downloadFile = async (f) => {
    try {
      const resp = await fetch(`${API_BASE}/critical-spares-obs-files/${f.id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!resp.ok) throw new Error("download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.filename || "attachment";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download file"); }
  };

  const addItem = async () => {
    if (!addForm.part_number.trim() || !addForm.fleet.trim()) { toast.error("Part number and fleet type are required"); return; }
    setSaving(true);
    try {
      await api.post("/critical-spares-obs", { part_number: addForm.part_number, fleet: addForm.fleet, description: addForm.description || null, note: addForm.note || null });
      toast.success("Added to obsolescence list");
      setAddForm({ part_number: "", fleet: "", description: "", note: "" });
      setShowAdd(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  const groups = useMemo(() => {
    const m = new Map();
    filtered.forEach((r) => {
      const k = r.fleet || "—";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    });
    return [...m.entries()];
  }, [filtered]);

  const exportReport = async (fmt = "xlsx") => {
    try {
      const path = fmt === "pdf" ? "/critical-spares-obs-export-pdf" : "/critical-spares-obs-export";
      const resp = await fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!resp.ok) throw new Error("export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Obsolescence-Report-${new Date().toISOString().slice(0, 10)}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch { toast.error("Failed to export report"); }
  };

  const removeItem = async (r) => {
    if (!window.confirm(`Remove ${r.part_number} from the obsolescence list?`)) return;
    try {
      await api.delete(`/critical-spares-obs/${r.id}`);
      toast.success("Removed");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove"); }
  };

  return (
    <div className="mt-5 space-y-4" data-testid="cs-obs-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="font-mono text-[11px] uppercase tracking-widest text-gray-500">
          {(items || []).length} part{(items || []).length === 1 ? "" : "s"} subject for obsolescence — full history of actions taken
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-7 font-mono text-[10px] uppercase tracking-widest" onClick={() => exportReport("xlsx")} data-testid="cs-obs-export-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Export XLSX
          </Button>
          <Button size="sm" variant="outline" className="h-7 font-mono text-[10px] uppercase tracking-widest" onClick={() => exportReport("pdf")} data-testid="cs-obs-export-pdf-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Export PDF
          </Button>
          {canEdit && (
            <Button size="sm" variant="outline" className="h-7 font-mono text-[10px] uppercase tracking-widest border-orange-300 text-orange-700 hover:bg-orange-50 hover:text-orange-800" onClick={() => setShowAdd((v) => !v)} data-testid="cs-obs-add-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add part
            </Button>
          )}
        </div>
      </div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input className="pl-9 h-9" placeholder="Search part number…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cs-obs-search" />
        {q && (
          <button type="button" className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600" onClick={() => setQ("")} data-testid="cs-obs-search-clear">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {showAdd && (
        <div className="border border-orange-200 bg-orange-50/40 rounded-lg p-4 grid grid-cols-2 gap-3" data-testid="cs-obs-add-form">
          <div className="space-y-1.5">
            <Label>Part number</Label>
            <Input value={addForm.part_number} onChange={(e) => setAddForm((f) => ({ ...f, part_number: e.target.value }))} data-testid="cs-obs-add-pn" />
          </div>
          <div className="space-y-1.5">
            <Label>Fleet type</Label>
            <Input list="cs-obs-fleets" value={addForm.fleet} onChange={(e) => setAddForm((f) => ({ ...f, fleet: e.target.value.toUpperCase() }))} data-testid="cs-obs-add-fleet" />
            <datalist id="cs-obs-fleets">{(fleets || []).map((f) => <option key={f} value={f} />)}</datalist>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Description (optional)</Label>
            <Input value={addForm.description} onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))} placeholder="e.g. Main wheel assembly" data-testid="cs-obs-add-description" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>First action note (optional)</Label>
            <Input value={addForm.note} onChange={(e) => setAddForm((f) => ({ ...f, note: e.target.value }))} placeholder="What was done / last action…" data-testid="cs-obs-add-note" />
          </div>
          <div className="col-span-2 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button size="sm" className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={addItem} disabled={saving} data-testid="cs-obs-add-save">
              {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Add
            </Button>
          </div>
        </div>
      )}
      {(items || []).length === 0 && (
        <div className="border border-dashed border-summit-hairline rounded-lg py-12 text-center text-sm text-gray-400" data-testid="cs-obs-empty">
          No parts flagged for obsolescence.
        </div>
      )}
      {(items || []).length > 0 && filtered.length === 0 && (
        <div className="border border-dashed border-summit-hairline rounded-lg py-12 text-center text-sm text-gray-400" data-testid="cs-obs-no-match">
          No parts match "{q}".
        </div>
      )}
      {groups.map(([fleet, rows]) => (
        <div key={fleet} className="space-y-3" data-testid={`cs-obs-group-${fleet}`}>
          <button
            type="button"
            className="w-full flex items-center gap-2.5 pt-1 group cursor-pointer"
            onClick={() => setCollapsed((m) => ({ ...m, [fleet]: !m[fleet] }))}
            title={collapsed[fleet] ? "Expand group" : "Collapse group"}
            data-testid={`cs-obs-group-toggle-${fleet}`}
          >
            <span className="font-mono text-[11px] uppercase tracking-widest font-semibold text-gray-700 group-hover:text-summit-primary transition-colors" data-testid={`cs-obs-group-label-${fleet}`}>{fleet}</span>
            <span className="font-mono text-[10px] text-gray-400">{rows.length}</span>
            <ChevronDown className={`w-3.5 h-3.5 text-gray-400 group-hover:text-summit-primary transition-transform ${collapsed[fleet] ? "-rotate-90" : ""}`} />
            <div className="h-px bg-summit-hairline flex-1" />
          </button>
          {!collapsed[fleet] && rows.map((r) => {
        const log = r.obs_log || [];
        return (
          <div key={r.id} className="border border-orange-200 rounded-lg bg-white" data-testid={`cs-obs-part-${r.id}`}>
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-orange-100 bg-orange-50/50 rounded-t-lg flex-wrap">
              <AlertTriangle className="w-3.5 h-3.5 text-orange-600" />
              <span className="font-mono text-sm font-semibold">{r.part_number}</span>
              {r.description && <span className="text-sm text-gray-600 truncate max-w-[320px]" data-testid={`cs-obs-desc-${r.id}`}>* {r.description}</span>}
              {canEdit && (
                <button type="button" className="ml-auto p-1.5 rounded text-gray-400 hover:text-rose-600" onClick={() => removeItem(r)} data-testid={`cs-obs-delete-${r.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="px-4 py-3">
              {log.length > 0 ? (
                <div className="space-y-1.5">
                  <div className="border border-orange-200 bg-orange-50/60 rounded-md px-2.5 py-1.5" data-testid={`cs-obs-last-action-${r.id}`}>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-orange-700">Last action · {fmtStamp(log[0])}</div>
                    {log[0].note && <div className="text-sm text-gray-800">{log[0].note}</div>}
                    {log[0].file && (
                      <button type="button" className="inline-flex items-center gap-1 mt-0.5 text-xs text-summit-primary hover:underline" onClick={() => downloadFile(log[0].file)} data-testid={`cs-obs-file-${r.id}-0`}>
                        <Paperclip className="w-3 h-3" /> {log[0].file.filename} · {Math.max(1, Math.round((log[0].file.size || 0) / 1024))} KB
                      </button>
                    )}
                  </div>
                  {log.slice(1).map((e, i) => (
                    <div key={i} className="px-2.5" data-testid={`cs-obs-log-${r.id}-${i}`}>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-gray-400">{fmtStamp(e)}</span>
                      {e.note && <span className="text-sm text-gray-600 ml-2">{e.note}</span>}
                      {e.file && (
                        <button type="button" className="inline-flex items-center gap-1 ml-2 text-xs text-summit-primary hover:underline align-middle" onClick={() => downloadFile(e.file)} data-testid={`cs-obs-file-${r.id}-${i + 1}`}>
                          <Paperclip className="w-3 h-3" /> {e.file.filename}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400" data-testid={`cs-obs-no-actions-${r.id}`}>No actions recorded yet.</p>
              )}
              {canEdit && (
                <div className="mt-2.5 space-y-1.5">
                  <div className="flex gap-2">
                    <Input
                      className="h-8 text-sm"
                      placeholder="What was done? (e.g. quote requested, replacement sourced…)"
                      value={notes[r.id] || ""}
                      onChange={(e) => setNotes((m) => ({ ...m, [r.id]: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && addNote(r)}
                      data-testid={`cs-obs-note-input-${r.id}`}
                    />
                    <input
                      type="file"
                      className="hidden"
                      ref={(el) => { fileInputs.current[r.id] = el; }}
                      onChange={(e) => setFiles((m) => ({ ...m, [r.id]: e.target.files?.[0] || null }))}
                      data-testid={`cs-obs-file-input-${r.id}`}
                    />
                    <Button size="sm" variant="outline" className="h-8 px-2.5" title="Attach file (max 5 MB)" onClick={() => fileInputs.current[r.id]?.click()} data-testid={`cs-obs-attach-btn-${r.id}`}>
                      <Paperclip className={`w-3.5 h-3.5 ${files[r.id] ? "text-summit-primary" : ""}`} />
                    </Button>
                    <Button size="sm" className="h-8 bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={() => addNote(r)} disabled={busy === r.id} data-testid={`cs-obs-note-add-${r.id}`}>
                      {busy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Add"}
                    </Button>
                  </div>
                  {files[r.id] && (
                    <div className="inline-flex items-center gap-1.5 border border-summit-hairline rounded-md px-2 py-1 text-xs text-gray-600" data-testid={`cs-obs-pending-file-${r.id}`}>
                      <Paperclip className="w-3 h-3 text-summit-primary" /> {files[r.id].name}
                      <button type="button" className="text-gray-400 hover:text-rose-600" onClick={() => { setFiles((m) => ({ ...m, [r.id]: null })); if (fileInputs.current[r.id]) fileInputs.current[r.id].value = ""; }} data-testid={`cs-obs-pending-file-clear-${r.id}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
          })}
        </div>
      ))}
    </div>
  );
}

function OOScanResultDialog({ result, onClose }) {
  return (
    <Dialog open={!!result} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="cs-oo-scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <PackageSearch className="w-5 h-5 text-sky-600" /> Open Orders scan — {result?.base}
          </DialogTitle>
          <DialogDescription className="sr-only">Open orders scan results</DialogDescription>
        </DialogHeader>
        {result && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600" data-testid="cs-oo-scan-summary">
              <strong>{result.updated}</strong> part{result.updated === 1 ? "" : "s"} matched open orders.
              {result.status_changed > 0 && <> {result.status_changed} flipped Out of stock → On order.</>}
              {" "}Other statuses stay as they are — the order info is just recorded. The file was read in your browser only.
            </p>
            {result.matched.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-sky-700">
                  <PackageSearch className="w-3.5 h-3.5" /> Open orders recorded
                </div>
                <div className="mt-1.5 space-y-1.5">
                  {result.matched.map((m) => (
                    <div key={m.id} className="border border-sky-100 bg-sky-50/50 rounded-md px-2.5 py-1.5" data-testid={`cs-oo-scan-matched-${m.id}`}>
                      <div className="font-mono text-xs font-semibold">
                        {m.part_number}
                        {m.status_changed && <span className="ml-2 font-mono text-[9px] uppercase tracking-widest text-sky-700">→ On order</span>}
                      </div>
                      {m.orders.map((o, i) => (
                        <div key={i} className="font-mono text-[10px] text-gray-600">{o}</div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {result.matched.length === 0 && (
              <p className="text-sm text-gray-400" data-testid="cs-oo-scan-empty">No column-P part numbers matched this base's master list.</p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={onClose} data-testid="cs-oo-scan-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewAllDialog({ base, rows, canEdit, onQuickStatus, onEdit, onDelete, onClose }) {
  const [q, setQ] = useState("");
  useEffect(() => { if (base) setQ(""); }, [base]);
  const list = useMemo(() => {
    if (!base) return [];
    const all = (rows || []).filter((r) => r.base === base);
    const s = q.trim().toLowerCase();
    const filtered = s
      ? all.filter((r) => [r.part_number, r.description, ...(r.alternates || [])].filter(Boolean).join(" ").toLowerCase().includes(s))
      : all;
    return [...filtered].sort(
      (x, y) => x.fleet.localeCompare(y.fleet) || (STATUS_ORDER[x.status] ?? 5) - (STATUS_ORDER[y.status] ?? 5) || x.part_number.localeCompare(y.part_number)
    );
  }, [base, rows, q]);
  return (
    <Dialog open={!!base} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cs-view-all-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <MapPin className="w-5 h-5 text-summit-primary" /> {base} — all parts ({list.length})
          </DialogTitle>
          <DialogDescription className="sr-only">All critical spares at this base</DialogDescription>
        </DialogHeader>
        <Input placeholder="Search this base…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cs-view-all-search" />
        <div className="divide-y divide-summit-hairline">
          {list.map((r) => (
            <div key={r.id} className="py-2 flex items-center gap-2 flex-wrap" data-testid={`cs-view-all-part-${r.id}`}>
              <span className="font-mono text-[9px] uppercase tracking-widest border border-summit-hairline rounded px-1.5 py-0.5 text-gray-500">{r.fleet}</span>
              <span className="font-mono text-sm font-semibold">{r.part_number}</span>
              {r.description && <span className="text-sm text-gray-600 truncate max-w-[180px]">{r.description}</span>}
              <div className="ml-auto flex items-center gap-1.5">
                {canEdit ? (
                  <Select value={r.status} onValueChange={(v) => onQuickStatus(r, v)}>
                    <SelectTrigger
                      className={`h-6 w-auto gap-1 px-2 py-0 rounded-full border font-mono text-[10px] uppercase tracking-widest focus:ring-0 ${isAltInStock(r, rows) ? ALT_IN_STOCK.cls : (STATUSES[r.status] || STATUSES.in_stock).cls}`}
                      data-testid={`cs-view-all-status-${r.id}`}
                    >
                      {isAltInStock(r, rows) ? <span>{ALT_IN_STOCK.label}</span> : <SelectValue />}
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <StatusBadge status={r.status} alt={isAltInStock(r, rows)} />
                )}
                {canEdit && (
                  <>
                    <button type="button" className="p-1 text-gray-400 hover:text-summit-primary" onClick={() => onEdit(r)} data-testid={`cs-view-all-edit-${r.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" className="p-1 text-gray-400 hover:text-rose-600" onClick={() => onDelete(r)} data-testid={`cs-view-all-delete-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
          {list.length === 0 && <p className="py-6 text-sm text-gray-400 text-center">No parts.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RepairScanResultDialog({ result, onClose }) {
  return (
    <Dialog open={!!result} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="cs-repair-scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Wrench className="w-5 h-5 text-amber-600" /> ER repair scan — {result?.base}
          </DialogTitle>
          <DialogDescription className="sr-only">Repair scan results</DialogDescription>
        </DialogHeader>
        {result && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600" data-testid="cs-repair-scan-summary">
              <strong>{result.updated}</strong> part{result.updated === 1 ? "" : "s"} marked Being repaired from ER rows. In-stock parts keep their status — their ER serials are just recorded. The file was read in your browser only.
            </p>
            {result.matched.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-amber-700">
                  <Wrench className="w-3.5 h-3.5" /> Now being repaired
                </div>
                <div className="mt-1.5 space-y-1.5">
                  {result.matched.map((m) => (
                    <div key={m.id} className="border border-amber-100 bg-amber-50/50 rounded-md px-2.5 py-1.5" data-testid={`cs-repair-scan-matched-${m.id}`}>
                      <div className="font-mono text-xs font-semibold">{m.part_number}</div>
                      {m.serials.map((s, i) => (
                        <div key={i} className="font-mono text-[10px] text-gray-600">S/N {s}</div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(result.in_stock_updated || []).length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-emerald-700" data-testid="cs-repair-scan-instock">
                  <CheckCircle2 className="w-3.5 h-3.5" /> In stock — repair serials added (status unchanged)
                </div>
                <div className="mt-1.5 space-y-1.5">
                  {result.in_stock_updated.map((m) => (
                    <div key={m.id} className="border border-emerald-100 bg-emerald-50/40 rounded-md px-2.5 py-1.5" data-testid={`cs-repair-scan-instock-${m.id}`}>
                      <div className="font-mono text-xs font-semibold">{m.part_number}</div>
                      {m.serials.map((s, i) => (
                        <div key={i} className="font-mono text-[10px] text-amber-700">S/N {s} — in repair</div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {result.matched.length === 0 && (result.in_stock_updated || []).length === 0 && (
              <p className="text-sm text-gray-400" data-testid="cs-repair-scan-empty">No ER rows matched not-in-stock parts on this base's master list.</p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={onClose} data-testid="cs-repair-scan-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScanHistoryDialog({ data, onClose }) {
  const [audit, setAudit] = useState(null);
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    if (data?.base) {
      setAudit(null);
      api.get(`/critical-spares/audit?base=${encodeURIComponent(data.base)}`).then((r) => setAudit(r.data || [])).catch(() => setAudit([]));
    }
  }, [data?.base]);
  const srcCls = (s) =>
    s === "crosscheck" || s === "general-scan" ? "bg-emerald-100 text-emerald-800"
      : s === "manual-edit" ? "bg-sky-100 text-sky-800"
      : s === "delete" ? "bg-rose-100 text-rose-700"
      : s === "repair-scan" || s === "general-repair-scan" ? "bg-amber-100 text-amber-800"
      : s === "oo-scan" || s === "general-oo-scan" ? "bg-sky-100 text-sky-800"
      : "bg-gray-100 text-gray-600";
  return (
    <Dialog open={!!data} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="cs-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <History className="w-5 h-5 text-summit-primary" /> Cross-check history — {data?.base}
          </DialogTitle>
          <DialogDescription className="sr-only">Past stock cross-checks</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {(data?.history || []).length === 0 && (
            <p className="text-sm text-gray-400 py-4 text-center" data-testid="cs-history-empty">No cross-checks recorded yet — history starts with the next scan.</p>
          )}
          {(data?.history || []).map((h, i) => (
            <div key={i} className="border border-summit-hairline rounded-md px-3 py-2" data-testid={`cs-history-entry-${i}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-widest text-gray-600">{fmtStamp(h)}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-700">{h.fleet ? `${h.fleet} · ` : ""}{h.updated}/{h.total_parts} in stock</span>
              </div>
              {(h.not_found || []).length > 0 ? (
                <div className="mt-1.5">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-rose-600">Missing ({h.not_found.length})</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {h.not_found.map((pn) => (
                      <span key={pn} className="font-mono text-[10px] border border-rose-200 bg-rose-50 text-rose-700 rounded px-1.5 py-0.5">{pn}</span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mt-1 font-mono text-[9px] uppercase tracking-widest text-emerald-600">No missing parts</div>
              )}
              {h.unknown_count > 0 && (
                <div className="mt-1 font-mono text-[9px] uppercase text-gray-400">{h.unknown_count} unknown PN{h.unknown_count === 1 ? "" : "s"} in file</div>
              )}
            </div>
          ))}
          <div className="pt-2 border-t border-summit-hairline">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Status change audit</div>
            {audit === null && <p className="text-xs text-gray-400 mt-1.5">Loading…</p>}
            {audit !== null && audit.length === 0 && (
              <p className="text-xs text-gray-400 mt-1.5" data-testid="cs-audit-empty">No status changes recorded yet — the audit trail starts with the next change.</p>
            )}
            {(audit || []).map((a) => (
              <div key={a.id} className="border border-summit-hairline rounded-md px-3 py-1.5 mt-1.5" data-testid={`cs-audit-${a.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-gray-600">{fmtStamp(a)}</span>
                  <span className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ${srcCls(a.source)}`}>{a.source}{a.fleet ? ` · ${a.fleet}` : ""} · {a.count}</span>
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {(a.changes || []).slice(0, expanded[a.id] ? 200 : 12).map((c, i) => (
                    <span key={i} className="font-mono text-[10px] border border-summit-hairline rounded px-1.5 py-0.5 text-gray-600">
                      {c.pn}: {c.from || "—"} → {c.to || "—"}
                    </span>
                  ))}
                  {(a.changes || []).length > 12 && (
                    <button
                      type="button"
                      className="text-[10px] text-summit-primary hover:underline self-center"
                      onClick={() => setExpanded((e) => ({ ...e, [a.id]: !e[a.id] }))}
                      data-testid={`cs-audit-expand-${a.id}`}
                    >
                      {expanded[a.id] ? "show less" : `+${(a.changes || []).length - 12} more`}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cs-history-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PartDialog({ open, onClose, onSaved, editing, fleets, bases, defaultFleet }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing
        ? { ...editing, qty: editing.qty ?? "", alternates_text: (editing.alternates || []).join("\n"), repair_serials: editing.repair_serials || [], pos: editing.pos || [] }
        : { base: "", fleet: defaultFleet || "", part_number: "", alternates_text: "", description: "", qty: "", status: "in_stock", obsolescence: false, notes: "", repair_serials: [], pos: [] });
    }
  }, [open, editing, defaultFleet]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updRow = (key, i, field, v) => setForm((f) => { const l = [...(f[key] || [])]; l[i] = { ...l[i], [field]: v }; return { ...f, [key]: l }; });
  const addRow = (key, row) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), row] }));
  const rmRow = (key, i) => setForm((f) => ({ ...f, [key]: (f[key] || []).filter((_, j) => j !== i) }));

  const save = async () => {
    if (!form.part_number?.trim()) { toast.error("Part number is required"); return; }
    if (!form.base?.trim() || !form.fleet?.trim()) { toast.error("Base and fleet type are required"); return; }
    setBusy(true);
    const payload = {
      base: form.base, fleet: form.fleet, part_number: form.part_number,
      alternates: (form.alternates_text || "").split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
      description: form.description || "", qty: form.qty === "" || form.qty == null ? null : Number(form.qty),
      status: form.status || "in_stock", obsolescence: !!form.obsolescence, notes: form.notes || null,
      oos_note: form.oos_note || null,
      repair_serials: (form.repair_serials || []).filter((s) => (s.serial || "").trim()),
      pos: (form.pos || []).filter((p) => (p.po || "").trim()),
    };
    try {
      if (editing) {
        await api.patch(`/critical-spares/${editing.id}`, payload);
        toast.success("Part updated");
      } else {
        await api.post("/critical-spares", payload);
        toast.success("Part added to master list");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="cs-part-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? "Edit master list part" : "Add part to master list"}</DialogTitle>
          <DialogDescription className="sr-only">Critical spare part form</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Base</Label>
            <Input list="cs-bases" value={form.base || ""} onChange={(e) => set("base", e.target.value.toUpperCase())} placeholder="e.g. YZF" data-testid="cs-form-base" />
            <datalist id="cs-bases">{bases.map((b) => <option key={b} value={b} />)}</datalist>
          </div>
          <div className="space-y-1.5">
            <Label>Fleet type</Label>
            <Input list="cs-fleets" value={form.fleet || ""} onChange={(e) => set("fleet", e.target.value.toUpperCase())} placeholder="e.g. AVRO RJ" data-testid="cs-form-fleet" />
            <datalist id="cs-fleets">{fleets.map((f) => <option key={f} value={f} />)}</datalist>
          </div>
          <div className="space-y-1.5">
            <Label>Part number</Label>
            <Input value={form.part_number || ""} onChange={(e) => set("part_number", e.target.value)} data-testid="cs-form-part-number" />
          </div>
          <div className="space-y-1.5">
            <Label>Qty</Label>
            <Input type="number" min="0" value={form.qty ?? ""} onChange={(e) => set("qty", e.target.value)} data-testid="cs-form-qty" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Alternate part numbers</Label>
            <Textarea rows={2} value={form.alternates_text || ""} onChange={(e) => set("alternates_text", e.target.value)} placeholder="One per line (or comma-separated) — each alternate becomes its own linked entry on the master list" data-testid="cs-form-alternates" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Description</Label>
            <Input value={form.description || ""} onChange={(e) => set("description", e.target.value)} placeholder="e.g. Main wheel assembly" data-testid="cs-form-description" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Status</Label>
            <Select value={form.status || "in_stock"} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="cs-form-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUSES).map(([k, v]) => (
                  <SelectItem key={k} value={k} data-testid={`cs-status-option-${k}`}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {form.status === "out_of_stock" && (
            <div className="col-span-2 space-y-1.5 border border-rose-200 bg-rose-50/40 rounded-md p-3" data-testid="cs-form-oos-section">
              <Label className="text-rose-800">Out-of-stock detail notes</Label>
              <Textarea rows={2} value={form.oos_note || ""} onChange={(e) => set("oos_note", e.target.value)} placeholder="What's the plan for this part — sourcing, quotes, AOG risk…" data-testid="cs-form-oos-note" />
            </div>
          )}
          {form.status === "repair" && (
            <div className="col-span-2 space-y-2 border border-amber-200 bg-amber-50/40 rounded-md p-3" data-testid="cs-form-repair-section">
              <div className="flex items-center justify-between">
                <Label className="text-amber-800">Serials in repair</Label>
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => addRow("repair_serials", { serial: "", note: "", eta: "" })} data-testid="cs-add-repair-serial">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add serial
                </Button>
              </div>
              {(form.repair_serials || []).map((s, i) => (
                <div key={i} className="grid grid-cols-[110px_1fr_130px_auto] gap-1.5 items-center">
                  <Input placeholder="Serial #" value={s.serial || ""} onChange={(e) => updRow("repair_serials", i, "serial", e.target.value)} data-testid={`cs-repair-serial-${i}`} />
                  <Input placeholder="What's happening with it…" value={s.note || ""} onChange={(e) => updRow("repair_serials", i, "note", e.target.value)} data-testid={`cs-repair-note-${i}`} />
                  <Input type="date" title="ETA" value={s.eta || ""} onChange={(e) => updRow("repair_serials", i, "eta", e.target.value)} data-testid={`cs-repair-eta-${i}`} />
                  <button type="button" className="p-1 text-gray-400 hover:text-rose-600" onClick={() => rmRow("repair_serials", i)} data-testid={`cs-repair-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {(form.repair_serials || []).length === 0 && (
                <p className="text-xs text-amber-700/70">Track each unit in repair — serial, what's happening with it, and its ETA.</p>
              )}
            </div>
          )}
          {form.status === "on_order" && (
            <div className="col-span-2 space-y-2 border border-sky-200 bg-sky-50/40 rounded-md p-3" data-testid="cs-form-po-section">
              <div className="flex items-center justify-between">
                <Label className="text-sky-800">Purchase orders</Label>
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => addRow("pos", { po: "", eta: "" })} data-testid="cs-add-po">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add PO
                </Button>
              </div>
              {(form.pos || []).map((p, i) => (
                <div key={i} className="grid grid-cols-[1fr_130px_auto] gap-1.5 items-center">
                  <Input placeholder="PO number / detail" value={p.po || ""} onChange={(e) => updRow("pos", i, "po", e.target.value)} data-testid={`cs-po-${i}`} />
                  <Input type="date" title="ETA" value={p.eta || ""} onChange={(e) => updRow("pos", i, "eta", e.target.value)} data-testid={`cs-po-eta-${i}`} />
                  <button type="button" className="p-1 text-gray-400 hover:text-rose-600" onClick={() => rmRow("pos", i)} data-testid={`cs-po-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {(form.pos || []).length === 0 && (
                <p className="text-xs text-sky-700/70">Track each PO raised for this part with its ETA.</p>
              )}
            </div>
          )}
          <label className="col-span-2 flex items-center gap-2.5 border border-summit-hairline rounded-md px-3 py-2.5 cursor-pointer" data-testid="cs-form-obsolescence-label">
            <Checkbox checked={!!form.obsolescence} onCheckedChange={(v) => set("obsolescence", !!v)} data-testid="cs-form-obsolescence" />
            <span className="text-sm flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-orange-600" /> Subject for obsolescence
            </span>
          </label>
          <div className="col-span-2 space-y-1.5">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Repair vendor, PO number, loan details…" data-testid="cs-form-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={save} disabled={busy} data-testid="cs-form-save">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            {editing ? "Save changes" : "Add part"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CriticalSpares() {
  const { user } = useAuth();
  const canEdit = user?.critical_spares_edit || user?.role === "admin";
  const [rows, setRows] = useState(null);
  const [fleet, setFleet] = useState("");
  const [aircraftFleets, setAircraftFleets] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [denied, setDenied] = useState(false);
  const [stamps, setStamps] = useState({});
  const [scanResult, setScanResult] = useState(null);
  const [repairScanResult, setRepairScanResult] = useState(null);
  const [masterScanResult, setMasterScanResult] = useState(null);
  const [ooScanResult, setOOScanResult] = useState(null);
  const [generalScanResult, setGeneralScanResult] = useState(null);
  const [generalRepairResult, setGeneralRepairResult] = useState(null);
  const [generalOOResult, setGeneralOOResult] = useState(null);
  const [histBase, setHistBase] = useState(null);
  const [baseSearch, setBaseSearch] = useState({});
  const [expandedBases, setExpandedBases] = useState({});
  const [tileFilter, setTileFilter] = useState(null);
  const [viewAllBase, setViewAllBase] = useState(null);
  const [obsItems, setObsItems] = useState(null);
  const [scanningBase, setScanningBase] = useState(null);
  const fileRef = useRef(null);
  const scanBaseRef = useRef(null);
  const scanTypeRef = useRef("stock");

  const load = async () => {
    try {
      const r = await api.get("/critical-spares");
      setRows((r.data || []).map((p) => ({ ...p, fleet: normFleet(p.fleet) })));
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      else toast.error(e?.response?.data?.detail || "Failed to load");
    }
  };
  const loadStamps = () => api.get("/critical-spares-crosschecks").then((r) => setStamps(r.data || {})).catch(() => {});
  const loadObs = () => api.get("/critical-spares-obs").then((r) => setObsItems(r.data || [])).catch(() => {});

  useEffect(() => {
    load();
    loadStamps();
    loadObs();
    api.get("/aircraft").then((r) => setAircraftFleets([...new Set((r.data || []).map((a) => normFleet(a.fleet)).filter(Boolean))])).catch(() => {});
  }, []);

  // Keep data fresh: refetch on window focus/visibility and every 60s,
  // so scans done elsewhere (other users/devices) show up automatically.
  useEffect(() => {
    const refresh = () => { if (!document.hidden) { load(); loadStamps(); } };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    const iv = setInterval(refresh, 60000);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
      clearInterval(iv);
    };
  }, []);

  // Refetch when switching tabs (e.g. jumping to SUMMARY right after a scan).
  const fleetSwitchRef = useRef(false);
  useEffect(() => {
    if (!fleet) return;
    if (!fleetSwitchRef.current) { fleetSwitchRef.current = true; return; }
    load();
    loadStamps();
  }, [fleet]);

  const fleets = useMemo(() => {
    const s = new Set(aircraftFleets);
    (rows || []).forEach((r) => s.add(r.fleet));
    return ["SUMMARY", "705", ...[...s].sort(), "OBSOLESCENCE"];
  }, [rows, aircraftFleets]);

  useEffect(() => {
    if (!fleet && fleets.length > 0) setFleet(fleets[0]);
  }, [fleets, fleet]);

  useEffect(() => {
    if (viewAllBase && rows && !rows.some((r) => r.base === viewAllBase)) setViewAllBase(null);
  }, [rows, viewAllBase]);

  const bases = useMemo(() => [...new Set((rows || []).map((r) => r.base))].sort(), [rows]);

  const fleetRows = useMemo(
    () => (rows || []).filter((r) => (fleet === "705" ? is705(r.fleet) : r.fleet === fleet)),
    [rows, fleet]
  );

  const computeStats = (rowsArr) => {
    // Unique part numbers only — the same PN stocked at multiple bases counts once.
    const sets = { total: new Set(), in_stock: new Set(), out: new Set(), repair: new Set(), on_order: new Set(), on_loan: new Set() };
    rowsArr.forEach((r) => {
      const pn = (r.part_number || "").toUpperCase();
      sets.total.add(pn);
      if (r.status === "in_stock") sets.in_stock.add(pn);
      else {
        if (!isAltInStock(r, rows || [])) sets.out.add(pn);
        if (sets[r.status]) sets[r.status].add(pn);
      }
    });
    return { total: sets.total.size, in_stock: sets.in_stock.size, out: sets.out.size, repair: sets.repair.size, on_order: sets.on_order.size, on_loan: sets.on_loan.size };
  };

  const tilesFor = (s) => [
    { name: "Crit spares", key: "all", value: s.total, color: "#0f172a" },
    { name: "In stock", key: "in_stock", value: s.in_stock, color: STATUSES.in_stock.color },
    { name: "Out of stock", key: "out", value: s.out, color: "#e11d48" },
    { name: "Being repaired", key: "repair", value: s.repair, color: STATUSES.repair.color },
    { name: "On order", key: "on_order", value: s.on_order, color: STATUSES.on_order.color },
    { name: "On loan", key: "on_loan", value: s.on_loan, color: STATUSES.on_loan.color },
  ];

  const tileMatch = (r) => {
    if (!tileFilter) return true;
    if (tileFilter.fleet && r.fleet !== tileFilter.fleet) return false;
    const k = tileFilter.key;
    if (k === "in_stock") return r.status === "in_stock";
    if (k === "out") return r.status !== "in_stock" && !isAltInStock(r, rows || []);
    return r.status === k;
  };

  const renderTiles = (t, groupFleet) => {
    const slug = (groupFleet || "").replace(/\s+/g, "-").toLowerCase();
    const isActive = (key) => tileFilter?.key === key && (tileFilter?.fleet || null) === (groupFleet || null);
    return (
      <>
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-3 mt-3">
          {t.map((d) => (
            <button
              type="button"
              key={d.name}
              onClick={() => setTileFilter((f) => (d.key === "all" || (f?.key === d.key && (f?.fleet || null) === (groupFleet || null)) ? null : { key: d.key, fleet: groupFleet || null }))}
              className={`text-center rounded-md py-1.5 transition-colors hover:bg-summit-glacier ${isActive(d.key) ? "ring-2 ring-summit-primary bg-summit-glacier" : ""}`}
              data-testid={`cs-stat-${groupFleet ? `${slug}-` : ""}${d.name.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="font-display text-2xl font-semibold" style={{ color: d.color }}>{d.value}</div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-gray-500">{d.name}</div>
            </button>
          ))}
        </div>
        {tileFilter && (tileFilter.fleet || null) === (groupFleet || null) && (
          <p className="font-mono text-[10px] uppercase tracking-widest text-summit-primary mt-2" data-testid="cs-tile-filter-hint">
            Filtering: {tileFilter.fleet ? `${tileFilter.fleet} — ` : ""}{t.find((x) => x.key === tileFilter.key)?.name} — click the counter again to clear
          </p>
        )}
      </>
    );
  };

  const renderReport = (groupFleet) => {
    const rowsArr = groupFleet ? fleetRows.filter((r) => r.fleet === groupFleet) : fleetRows;
    const t = tilesFor(computeStats(rowsArr));
    return (
      <div key={groupFleet || "all"} className="border border-summit-hairline rounded-lg bg-white p-5 mt-5" data-testid="cs-report">
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{groupFleet || fleet} — status report · click a counter to filter</div>
        {renderTiles(t, groupFleet)}
      </div>
    );
  };

  const render705Fleet = (g) => {
    const gRows = fleetRows.filter((r) => r.fleet === g);
    const slug = g.replace(/\s+/g, "-").toLowerCase();
    const t = tilesFor(computeStats(gRows));
    const gBases = {};
    gRows.forEach((r) => { (gBases[r.base] ||= []).push(r); });
    const baseEntries = Object.entries(gBases).sort(([a], [b]) => a.localeCompare(b));
    return (
      <div key={g} className="border border-summit-hairline rounded-lg bg-white p-5 mt-5" data-testid={`cs-705-fleet-${slug}`}>
        <div className="font-display text-xl font-semibold tracking-tight" data-testid={`cs-705-fleet-header-${slug}`}>{g}</div>
        {baseEntries.length === 0 ? (
          <p className="text-sm text-gray-400 mt-3">No critical spares recorded yet.</p>
        ) : (
          <div className="flex flex-wrap gap-4 mt-3">
            {baseEntries.map(([base, parts]) => (
              <div key={base} className="border border-summit-hairline rounded-md pt-2 px-2" data-testid={`cs-705-pie-${slug}-${base}`}>
                <div className="flex items-center justify-center gap-1.5 font-display font-semibold text-sm">
                  <MapPin className="w-3.5 h-3.5 text-summit-primary" /> {base}
                </div>
                <BasePie parts={parts} base={`${slug}-${base}`} />
              </div>
            ))}
          </div>
        )}
        <div className="border-t border-summit-hairline mt-4 pt-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Status counters by Part Numbers · click to see part details</div>
          {renderTiles(t, g)}
        </div>
      </div>
    );
  };

  const byBase = useMemo(() => {
    const m = {};
    fleetRows.forEach((r) => { (m[r.base] ||= []).push(r); });
    return Object.entries(m)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([base, parts]) => {
        // Cluster alternate PNs together (union-find); groups sort by their best status.
        const byPN = {};
        parts.forEach((p) => { byPN[p.part_number.toUpperCase()] = p; });
        const parent = {};
        parts.forEach((p) => { parent[p.id] = p.id; });
        const find = (x) => (parent[x] === x ? x : (parent[x] = find(parent[x])));
        parts.forEach((p) => (p.alternates || []).forEach((a) => {
          const q = byPN[(a || "").toUpperCase()];
          if (q) { const ra = find(p.id), rb = find(q.id); if (ra !== rb) parent[rb] = ra; }
        }));
        const groups = {};
        parts.forEach((p) => { (groups[find(p.id)] ||= []).push(p); });
        const ord = (p) => STATUS_ORDER[p.status] ?? 5;
        const clusters = Object.values(groups).map((g) =>
          g.sort((x, y) => ord(x) - ord(y) || x.part_number.localeCompare(y.part_number))
        );
        clusters.sort((a, b) => {
          const ka = Math.max(...a.map(ord));
          const kb = Math.max(...b.map(ord));
          return ka - kb || a[0].part_number.localeCompare(b[0].part_number);
        });
        return [base, clusters.flatMap((g) => (g.length > 1 ? g.map((p) => ({ ...p, _alt_group: find(p.id) })) : g))];
      });
  }, [fleetRows]);

  const altsInStock = (r) => {
    const alts = (r.alternates || []).map((a) => a.toUpperCase());
    if (!alts.length) return [];
    return (rows || [])
      .filter((x) => x.id !== r.id && x.base === r.base && x.status === "in_stock" && alts.includes(x.part_number.toUpperCase()))
      .map((x) => x.part_number);
  };

  const otherBaseStock = (r) =>
    (rows || []).filter((x) => x.base !== r.base && x.status === "in_stock" && x.part_number.toUpperCase() === r.part_number.toUpperCase());

  const partMatches = (r, q) => {
    const s = (q || "").trim().toLowerCase();
    if (!s) return true;
    return [
      r.part_number, r.description, r.notes, r.oos_note,
      ...(r.alternates || []),
      ...(r.serials || []).map((x) => x.serial),
      ...(r.repair_serials || []).map((x) => `${x.serial} ${x.note || ""}`),
      ...(r.pos || []).map((x) => x.po),
      ...(r.open_orders || []),
    ].filter(Boolean).join(" ").toLowerCase().includes(s);
  };

  const startScan = (base, type = "stock") => {
    scanBaseRef.current = base;
    scanTypeRef.current = type;
    fileRef.current?.click();
  };

  const onScanFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const base = scanBaseRef.current;
    if (!file || (!base && !scanTypeRef.current.startsWith("general"))) return;
    setScanningBase(base || "__ALL__");
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      if (!ws || !ws["!ref"]) { toast.error("The file appears to be empty."); return; }
      const range = XLSX.utils.decode_range(ws["!ref"]);
      const cellAt = (c, r) => {
        const v = ws[XLSX.utils.encode_cell({ c, r })]?.v;
        return v == null ? "" : String(v).trim();
      };
      if (scanTypeRef.current === "general") {
        const entries = [];
        for (let r = 1; r <= range.e.r; r++) {
          const pn = cellAt(3, r); // col D
          if (!pn) continue;
          entries.push({ part_number: pn, serial: cellAt(4, r), location: cellAt(10, r), base_raw: cellAt(8, r) }); // E, K, I
        }
        if (entries.length === 0) { toast.error("No part numbers found in column D (data starts on row 2)."); return; }
        const res = await api.post("/critical-spares/general-scan", { entries });
        setGeneralScanResult(res.data);
        load();
        loadStamps();
        return;
      }
      if (scanTypeRef.current === "general_repair") {
        const entries = [];
        for (let r = 1; r <= range.e.r; r++) {
          const pn = cellAt(3, r); // col D
          if (!pn) continue;
          if (cellAt(9, r).toUpperCase() !== "ER") continue; // col J must be ER
          entries.push({ part_number: pn, serial: cellAt(4, r) }); // col E
        }
        if (entries.length === 0) { toast.error("No rows with ER status (column J) found."); return; }
        const res = await api.post("/critical-spares/general-repair-scan", { entries });
        setGeneralRepairResult(res.data);
        load();
        return;
      }
      if (scanTypeRef.current === "general_oo") {
        const entries = [];
        for (let r = 1; r <= range.e.r; r++) {
          const pn = cellAt(15, r); // col P
          if (!pn) continue;
          entries.push({ part_number: pn, order_info: cellAt(1, r) }); // col B
        }
        if (entries.length === 0) { toast.error("No part numbers found in column P (data starts on row 2)."); return; }
        const res = await api.post("/critical-spares/general-oo-scan", { entries });
        setGeneralOOResult(res.data);
        load();
        return;
      }
      if (scanTypeRef.current === "master") {
        const entries = [];
        for (let r = 1; r <= range.e.r; r++) {
          const pn = cellAt(0, r); // col A: part number
          if (!pn) continue;
          entries.push({ part_number: pn, description: cellAt(1, r) }); // col B: description
        }
        if (entries.length === 0) { toast.error("No part numbers found in column A (data starts on row 2)."); return; }
        const res = await api.post("/critical-spares/master-scan", { base, fleet, entries });
        setMasterScanResult(res.data);
        load();
        return;
      }
      if (scanTypeRef.current === "oo") {
        const entries = [];
        for (let r = 1; r <= range.e.r; r++) {
          const pn = cellAt(15, r); // col P: part number
          if (!pn) continue;
          entries.push({ part_number: pn, order_info: cellAt(1, r) }); // col B: order info
        }
        if (entries.length === 0) { toast.error("No part numbers found in column P (data starts on row 2)."); return; }
        const res = await api.post("/critical-spares/oo-scan", { base, fleet, entries });
        setOOScanResult(res.data);
        load();
        return;
      }
      if (scanTypeRef.current === "repair") {
        const entries = [];
        for (let r = 1; r <= range.e.r; r++) {
          const pn = cellAt(3, r); // col D
          if (!pn) continue;
          if (cellAt(9, r).toUpperCase() !== "ER") continue; // col J must be ER
          entries.push({ part_number: pn, serial: cellAt(4, r) }); // col E
        }
        if (entries.length === 0) { toast.error("No rows with ER status (column J) found."); return; }
        const res = await api.post("/critical-spares/repair-scan", { base, fleet, entries });
        setRepairScanResult(res.data);
        load();
        return;
      }
      const entries = [];
      for (let r = 1; r <= range.e.r; r++) {
        const pn = cellAt(3, r); // col D
        if (!pn) continue;
        entries.push({ part_number: pn, serial: cellAt(4, r), location: cellAt(10, r) }); // E, K
      }
      if (entries.length === 0) { toast.error("No part numbers found in column D (data starts on row 2)."); return; }
      const res = await api.post("/critical-spares/crosscheck", { base, fleet, entries });
      setScanResult(res.data);
      load();
      loadStamps();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read that file — make sure it's a valid .xlsx.");
    } finally {
      setScanningBase(null);
    }
  };

  const quickStatus = async (r, status) => {
    try {
      await api.patch(`/critical-spares/${r.id}`, { status });
      setRows((rs) => (rs || []).map((x) => (x.id === r.id ? { ...x, status } : x)));
      toast.success(`${r.part_number} → ${STATUSES[status]?.label || status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update status"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.part_number} from the master list?`)) return;
    try {
      await api.delete(`/critical-spares/${r.id}`);
      toast.success("Part deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  if (denied) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="cs-no-access">
        <Boxes className="w-10 h-10 mx-auto text-gray-300" />
        <p className="text-sm text-gray-500 mt-4">You don't have access to Critical Spares. Ask an admin to grant it in Admin Settings.</p>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto px-4 sm:px-[15%] py-8" data-testid="critical-spares-page">
      <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onScanFile} data-testid="cs-scan-file-input" />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight flex items-center gap-2.5">
            <Boxes className="w-7 h-7 text-summit-primary" /> Critical Spares Status
          </h1>
          <p className="font-mono text-[11px] uppercase tracking-widest text-gray-500 mt-1">Master list of critical spares per base · grouped by fleet type</p>
        </div>
        {canEdit ? (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              className="font-mono text-[11px] uppercase tracking-widest border-summit-primary/40 text-summit-primary hover:bg-summit-glacier hover:text-summit-primary"
              onClick={() => startScan(null, "general")}
              disabled={scanningBase === "__ALL__"}
              data-testid="cs-general-scan-btn"
            >
              {scanningBase === "__ALL__" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ScanLine className="w-4 h-4 mr-1.5" />}
              General scan
            </Button>
            <Button
              variant="outline"
              className="font-mono text-[11px] uppercase tracking-widest border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
              onClick={() => startScan(null, "general_repair")}
              disabled={scanningBase === "__ALL__"}
              data-testid="cs-general-er-scan-btn"
            >
              <Wrench className="w-4 h-4 mr-1.5" />
              General ER
            </Button>
            <Button
              variant="outline"
              className="font-mono text-[11px] uppercase tracking-widest border-sky-300 text-sky-700 hover:bg-sky-50 hover:text-sky-800"
              onClick={() => startScan(null, "general_oo")}
              disabled={scanningBase === "__ALL__"}
              data-testid="cs-general-oo-scan-btn"
            >
              <PackageSearch className="w-4 h-4 mr-1.5" />
              General OO
            </Button>
            <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="cs-add-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add part
            </Button>
          </div>
        ) : (
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400 border border-summit-hairline rounded-full px-3 py-1.5" data-testid="cs-view-only-badge">View only</span>
        )}
      </div>

      <div className="flex items-center gap-2 mt-6 flex-wrap" data-testid="cs-fleet-buttons">
        {fleets.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => { setFleet(f); setTileFilter(null); }}
            data-testid={`cs-fleet-btn-${f.replace(/\s+/g, "-")}`}
            className={`px-4 py-1.5 rounded-full border font-mono text-[11px] uppercase tracking-widest transition-colors ${
              fleet === f ? "bg-summit-primary border-summit-primary text-white" : "border-summit-hairline text-gray-600 hover:bg-summit-glacier"
            }`}
          >
            {f} {f !== "SUMMARY" && <span className="opacity-60">{f === "OBSOLESCENCE" ? (obsItems || []).length : new Set((rows || []).filter((r) => (f === "705" ? is705(r.fleet) : r.fleet === f)).map((r) => (r.part_number || "").toUpperCase())).size}</span>}
          </button>
        ))}
        {fleets.length === 0 && rows !== null && <span className="text-sm text-gray-400">No fleet types yet — add your first part.</span>}
      </div>

      {rows === null ? (
        <div className="py-16 text-center text-gray-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
      ) : fleet === "SUMMARY" ? (
        <SummaryPanel rows={rows} />
      ) : fleet === "OBSOLESCENCE" ? (
        <ObsolescencePanel items={obsItems} canEdit={canEdit} fleets={fleets.filter((f) => f !== "705" && f !== "OBSOLESCENCE" && f !== "SUMMARY")} onChanged={loadObs} />
      ) : (
        <>
          {fleet && fleet !== "705" && renderReport(null)}
          {fleet === "705" && fleets.filter((f) => f !== "705" && is705(f)).map((g) => render705Fleet(g))}

          {(fleet !== "705" || tileFilter) && (
          <div className="mt-6 space-y-5">
            {byBase.length === 0 && fleet && (
              <div className="border border-dashed border-summit-hairline rounded-lg py-12 text-center text-sm text-gray-400" data-testid="cs-empty">
                No critical spares recorded for {fleet} yet.
              </div>
            )}
            {byBase.map(([base, parts]) => {
              const tileParts = tileFilter ? parts.filter(tileMatch) : parts;
              if (tileFilter && tileParts.length === 0) return null;
              const visible = tileParts.filter((r) => partMatches(r, baseSearch[base]));
              const shown = expandedBases[base] ? visible : visible.slice(0, 30);
              return (
              <div key={base} className="border border-summit-hairline rounded-lg bg-white" data-testid={`cs-base-${base}`}>
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-summit-hairline bg-summit-glacier/50 rounded-t-lg flex-wrap">
                  <MapPin className="w-3.5 h-3.5 text-summit-primary" />
                  <span className="font-display font-semibold">{base}</span>
                  <span className="font-mono text-[10px] text-gray-500 uppercase">{parts.length} part{parts.length === 1 ? "" : "s"} · master list</span>
                  <button
                    type="button"
                    className="font-mono text-[10px] uppercase tracking-widest text-summit-primary hover:underline"
                    onClick={() => setViewAllBase(base)}
                    data-testid={`cs-view-all-btn-${base}`}
                  >
                    view all
                  </button>
                  <div className="ml-auto flex items-center gap-3 flex-wrap">
                    <Input
                      className="h-7 w-44 text-xs"
                      placeholder="Search parts…"
                      value={baseSearch[base] || ""}
                      onChange={(e) => setBaseSearch((m) => ({ ...m, [base]: e.target.value }))}
                      data-testid={`cs-search-${base}`}
                    />
                    {stamps[base] && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-gray-500 hover:text-summit-primary hover:underline"
                        onClick={() => setHistBase({ base, history: stamps[base].history || [] })}
                        title="View cross-check history"
                        data-testid={`cs-crosscheck-stamp-${base}`}
                      >
                        <History className="w-3 h-3" />
                        Last cross-check: {fmtStamp(stamps[base])}
                      </button>
                    )}
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-gray-500 hover:text-summit-primary hover:underline"
                      onClick={() => setHistBase({ base, history: (stamps[base] && stamps[base].history) || [] })}
                      title="View the full status-change audit trail for this base"
                      data-testid={`cs-audit-btn-${base}`}
                    >
                      <History className="w-3 h-3" />
                      Audit
                    </button>
                    {canEdit && fleet !== "705" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 font-mono text-[10px] uppercase tracking-widest border-summit-primary/40 text-summit-primary hover:bg-summit-glacier"
                        onClick={() => startScan(base, "master")}
                        disabled={scanningBase === base}
                        data-testid={`cs-master-scan-btn-${base}`}
                      >
                        <ListPlus className="w-3.5 h-3.5 mr-1" />
                        Master scan
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row">
                  <BasePie parts={parts} base={base} />
                  <div className="divide-y divide-summit-hairline flex-1 sm:border-l border-summit-hairline">
                    {visible.length === 0 && (
                      <p className="px-4 py-6 text-sm text-gray-400" data-testid={`cs-search-no-match-${base}`}>No parts match.</p>
                    )}
                    {shown.map((r, ri) => {
                      const grp = r._alt_group;
                      const prevSame = !!grp && ri > 0 && shown[ri - 1]._alt_group === grp;
                      const nextSame = !!grp && ri < shown.length - 1 && shown[ri + 1]._alt_group === grp;
                      const linked = prevSame || nextSame;
                      return (
                      <div key={r.id} className={`px-4 py-2.5 flex items-center gap-3 flex-wrap ${linked ? "relative pl-9" : ""}`} data-testid={`cs-part-${r.id}`}>
                        {linked && (
                          <div className="absolute left-3.5 top-0 bottom-0 w-3 pointer-events-none" title="Alternate parts group" data-testid={`cs-alt-link-${r.id}`}>
                            <div className="absolute left-0 w-px bg-teal-400" style={{ top: prevSame ? "-1px" : "50%", bottom: nextSame ? "-1px" : "50%" }} />
                            <div className="absolute left-0 top-1/2 w-2 h-px bg-teal-400" />
                            {!prevSame && <Link2 className="w-3 h-3 text-teal-500 absolute -left-[5.5px] top-1/2 -translate-y-1/2 bg-white" />}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            {fleet === "705" && (
                              <span className="font-mono text-[9px] uppercase tracking-widest border border-summit-hairline rounded px-1 py-0.5 text-gray-400">{r.fleet}</span>
                            )}
                            <span className="font-mono text-sm font-semibold">{r.part_number}</span>
                            {r.obsolescence && (
                              <span title="Subject for obsolescence" data-testid={`cs-obsolescence-flag-${r.id}`}>
                                <AlertTriangle className="w-3.5 h-3.5 text-orange-600" />
                              </span>
                            )}
                            {r.description && <span className="text-sm text-gray-600 truncate max-w-[260px]" data-testid={`cs-description-${r.id}`}>{r.description}</span>}
                            {r.qty != null && <span className="font-mono text-[10px] text-gray-500">×{r.qty}</span>}
                            {(r.min_qty != null || r.max_qty != null) && (
                              <span className="font-mono text-[10px] text-gray-500 border border-summit-hairline rounded px-1 py-0.5" data-testid={`cs-minmax-${r.id}`}>
                                min {r.min_qty ?? "—"} · max {r.max_qty ?? "—"}
                              </span>
                            )}
                            {canEdit ? (
                              <Select value={r.status} onValueChange={(v) => quickStatus(r, v)}>
                                <SelectTrigger
                                  className={`h-6 w-auto gap-1 px-2 py-0 rounded-full border font-mono text-[10px] uppercase tracking-widest focus:ring-0 ${altsInStock(r).length > 0 && r.status !== "in_stock" ? ALT_IN_STOCK.cls : (STATUSES[r.status] || STATUSES.in_stock).cls}`}
                                  data-testid={`cs-quick-status-${r.id}`}
                                >
                                  {altsInStock(r).length > 0 && r.status !== "in_stock" ? <span>{ALT_IN_STOCK.label}</span> : <SelectValue />}
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.entries(STATUSES).map(([k, v]) => (
                                    <SelectItem key={k} value={k} data-testid={`cs-quick-status-option-${r.id}-${k}`}>{v.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <StatusBadge status={r.status} alt={altsInStock(r).length > 0 && r.status !== "in_stock"} />
                            )}
                            <AvailBadge pn={r.part_number} />
                          </div>
                          {(r.alternates || []).length > 0 && (
                            <div className="mt-0.5" data-testid={`cs-alternates-${r.id}`}>
                              {r.alternates.map((a) => (
                                <div key={a} className="font-mono text-[10px] text-gray-500">Alt: {a}</div>
                              ))}
                            </div>
                          )}
                          {(r.serials || []).length > 0 && r.status === "in_stock" && (
                            <div className="mt-0.5" data-testid={`cs-serials-${r.id}`}>
                              {r.serials.map((s, i) => (
                                <div key={i} className="font-mono text-[10px] text-emerald-700">
                                  S/N {s.serial || "—"}{s.location ? ` · ${s.location}` : ""}
                                </div>
                              ))}
                            </div>
                          )}
                          {altsInStock(r).length > 0 && (
                            <div className="font-mono text-[10px] text-emerald-600 mt-0.5" data-testid={`cs-alt-in-stock-${r.id}`}>
                              Alternate {altsInStock(r).join(", ")} in stock
                            </div>
                          )}
                          {r.status !== "in_stock" && otherBaseStock(r).length > 0 && (
                            <div className="font-mono text-[10px] text-indigo-600 mt-0.5" data-testid={`cs-other-base-stock-${r.id}`}>
                              {otherBaseStock(r)
                                .map((x) => `In stock at ${x.base}${(x.serials || []).length ? ` ×${x.serials.length}` : x.qty != null ? ` ×${x.qty}` : ""}`)
                                .join(" · ")}{" — potential stock transfer"}
                            </div>
                          )}
                          {r.status === "out_of_stock" && r.oos_note && (
                            <div className="font-mono text-[10px] text-rose-700 mt-0.5" data-testid={`cs-oos-note-${r.id}`}>{r.oos_note}</div>
                          )}
                          {(r.repair_serials || []).length > 0 && (
                            <div className="mt-0.5" data-testid={`cs-repair-serials-${r.id}`}>
                              <div className="font-mono text-[10px] uppercase tracking-widest text-amber-700">On Repairs:</div>
                              {r.repair_serials.map((s, i) => (
                                <div key={i} className="font-mono text-[10px] text-amber-700">
                                  S/N {s.serial}{s.eta ? ` · ETA ${s.eta}` : ""}{s.note ? ` — ${s.note}` : ""}{r.status === "in_stock" ? " · in repair" : ""}
                                </div>
                              ))}
                            </div>
                          )}
                          {r.status === "on_order" && (r.pos || []).length > 0 && (
                            <div className="mt-0.5" data-testid={`cs-pos-${r.id}`}>
                              {r.pos.map((p, i) => (
                                <div key={i} className="font-mono text-[10px] text-sky-700">
                                  PO {p.po}{p.eta ? ` · ETA ${p.eta}` : ""}
                                </div>
                              ))}
                            </div>
                          )}
                          {(r.open_orders || []).length > 0 && (
                            <div className="mt-0.5" data-testid={`cs-open-orders-${r.id}`}>
                              <div className="font-mono text-[10px] uppercase tracking-widest text-sky-700">Open Orders:</div>
                              {r.open_orders.map((o, i) => (
                                <OpenOrderLine key={i} row={r} order={o} canEdit={canEdit} onSaved={load} />
                              ))}
                            </div>
                          )}
                          {r.notes && <div className="text-xs text-violet-700 font-medium truncate" title={r.notes} data-testid={`cs-notes-${r.id}`}>{r.notes}</div>}
                          <div className="font-mono text-[9px] uppercase text-gray-400 mt-0.5">Updated {r.updated_by_name} · {new Date(r.updated_at).toLocaleDateString()}</div>
                        </div>
                        {canEdit && (
                          <div className="flex items-center gap-1 shrink-0">
                            <button type="button" className="p-1.5 rounded text-gray-400 hover:text-summit-primary" onClick={() => { setEditing(r); setDialogOpen(true); }} data-testid={`cs-edit-${r.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button type="button" className="p-1.5 rounded text-gray-400 hover:text-rose-600" onClick={() => remove(r)} data-testid={`cs-delete-${r.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                      );
                    })}
                    {visible.length > 30 && (
                      <div className="px-4 py-3 text-center">
                        <button
                          type="button"
                          className="font-mono text-[11px] uppercase tracking-widest text-summit-primary hover:underline"
                          onClick={() => setExpandedBases((m) => ({ ...m, [base]: !m[base] }))}
                          data-testid={`cs-show-all-${base}`}
                        >
                          {expandedBases[base] ? "Show less" : `Show all (${visible.length - 30} more)`}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
          )}
        </>
      )}

      <PartDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
        editing={editing}
        fleets={fleets.filter((f) => f !== "705" && f !== "OBSOLESCENCE" && f !== "SUMMARY")}
        bases={bases}
        defaultFleet={fleet === "705" || fleet === "OBSOLESCENCE" || fleet === "SUMMARY" ? "" : fleet}
      />
      <ViewAllDialog
        base={viewAllBase}
        rows={rows}
        canEdit={canEdit}
        onQuickStatus={quickStatus}
        onEdit={(r) => { setEditing(r); setDialogOpen(true); }}
        onDelete={remove}
        onClose={() => setViewAllBase(null)}
      />
      <ScanResultDialog result={scanResult} onClose={() => setScanResult(null)} />
      <RepairScanResultDialog result={repairScanResult} onClose={() => setRepairScanResult(null)} />
      <OOScanResultDialog result={ooScanResult} onClose={() => setOOScanResult(null)} />
      <MasterScanResultDialog result={masterScanResult} onClose={() => setMasterScanResult(null)} />
      <GeneralScanDialog result={generalScanResult} onClose={() => setGeneralScanResult(null)} />
      <GeneralRepairDialog result={generalRepairResult} onClose={() => setGeneralRepairResult(null)} />
      <GeneralOODialog result={generalOOResult} onClose={() => setGeneralOOResult(null)} />
      <ScanHistoryDialog data={histBase} onClose={() => setHistBase(null)} />
    </div>
  );
}
